import { useState, type Dispatch, type SetStateAction } from 'react';
import { Autocomplete, TextField, InputAdornment, IconButton, CircularProgress, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { CatalogField } from '@replayops/contracts';
import { api } from '../api/client.js';
import { useT } from '../i18n/useT.js';

interface Option {
  value: string;
  label: string;
  /** Marks the synthetic "create this" row the list grows when nothing matches. */
  create?: boolean;
}

/**
 * One field of an integration's config.
 *
 * Fields the catalogue marks with `options` are asked of the vendor rather than
 * typed: GitHub's owner is a login and Jira's project is a key, and a plausible
 * guess — a display name, a project's title — fails later as a 404 that reads
 * like a permissions problem. The list loads as soon as the credentials it
 * depends on are filled, and the field stays free-text so an answer the list
 * does not contain is still reachable.
 */
export function IntegrationField({
  field,
  provider,
  form,
  setForm,
  base,
}: {
  field: CatalogField;
  provider: string;
  form: Record<string, string>;
  setForm: Dispatch<SetStateAction<Record<string, string>>>;
  /** `/v1/orgs/{org}/projects/{project}/integrations` */
  base: string;
}) {
  const t = useT().replay.integrationField;
  const [options, setOptions] = useState<Option[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<string | null>(null);

  const label = field.optional ? `${field.label} (optional)` : field.label;
  const deps = field.options?.dependsOn ?? [];
  const ready = deps.every((d) => (form[d] ?? '').trim().length > 0);

  async function load() {
    if (!field.options || !ready) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.post(`${base}/${provider}/options`, {
        source: field.options.source,
        config: form,
      });
      setOptions(res.data.options as Option[]);
    } catch (e) {
      // A response outside our error shape (a 404 from a mis-built URL, a
      // proxy's HTML) must still say *something* diagnosable, not just shrug.
      const err = e as { response?: { status?: number; data?: { error?: { message?: string } } } };
      setError(
        err.response?.data?.error?.message ??
          (err.response?.status ? t.loadFailedStatus(err.response.status) : t.loadFailed),
      );
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Makes the thing the field points at — a repository the vendor does not
   * have yet. Runs only from the explicit "Create" row in the list, because it
   * writes into the customer's account on the other side.
   */
  async function create(value: string) {
    if (!field.options || creating) return;
    setCreating(true);
    setError(null);
    setCreated(null);
    try {
      const res = await api.post(`${base}/${provider}/options/create`, {
        source: field.options.source,
        value,
        config: form,
      });
      const made = res.data.option as Option;
      // The vendor may normalise the name; the config must hold what exists.
      setForm((s) => ({ ...s, [field.name]: made.value }));
      setOptions((prev) => [...(prev ?? []), made]);
      setCreated(made.label);
    } catch (e) {
      const err = e as { response?: { data?: { error?: { message?: string } } } };
      setError(err.response?.data?.error?.message ?? t.createFailed);
    } finally {
      setCreating(false);
    }
  }

  if (!field.options) {
    return (
      <TextField
        fullWidth
        margin="normal"
        label={label}
        type={field.secret ? 'password' : (field.type ?? 'text')}
        placeholder={field.placeholder}
        helperText={field.help}
        value={form[field.name] ?? ''}
        onChange={(e) => setForm((s) => ({ ...s, [field.name]: e.target.value }))}
      />
    );
  }

  const hint = error
    ? error
    : created
      ? `Created ${created}. Private, with issues enabled.`
      : !ready
        ? `Fill in ${deps.join(', ')} first, then pick from the list.`
        : (field.help ?? t.pickOrType);

  return (
    <Autocomplete
      freeSolo
      options={options ?? []}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        const shown = q ? opts.filter((o) => o.label.toLowerCase().includes(q)) : opts;
        // Offer to create what was typed, once the list is in and nothing
        // matches it exactly — never as a default.
        if (
          field.options?.creatable &&
          options !== null &&
          q &&
          !opts.some((o) => o.value.toLowerCase() === q)
        ) {
          return [...shown, { value: state.inputValue.trim(), label: `Create “${state.inputValue.trim()}”`, create: true }];
        }
        return shown;
      }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.create ? o.value : o.label)}
      // getOptionLabel doubles as the input text on selection, so for the
      // create row it must be the bare value — which means the listbox needs
      // its own renderer to say what the row actually does.
      renderOption={(props, o) => (
        <li {...props} key={o.create ? '__create__' : o.value}>
          {o.create ? <strong>{o.label}</strong> : o.label}
        </li>
      )}
      isOptionEqualToValue={(o, v) => o.value === (typeof v === 'string' ? v : v.value)}
      value={form[field.name] ?? ''}
      // The list is loaded on open rather than on every keystroke: these calls
      // hit the customer's vendor with their credentials, so one per open.
      onOpen={() => {
        if (options === null) void load();
      }}
      onInputChange={(_, v) => setForm((s) => ({ ...s, [field.name]: v }))}
      onChange={(_, v) => {
        if (v && typeof v !== 'string' && v.create) {
          void create(v.value);
          return;
        }
        setForm((s) => ({ ...s, [field.name]: typeof v === 'string' ? v : (v?.value ?? '') }));
      }}
      disabled={!ready}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          margin="normal"
          label={label}
          placeholder={field.placeholder}
          helperText={hint}
          error={!!error}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading || creating ? <CircularProgress size={16} /> : null}
                {ready && !loading && (
                  <InputAdornment position="end">
                    <Tooltip title={t.reload}>
                      <IconButton size="small" onClick={() => void load()}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
